import { loadTelegramSdk } from '@/appType/appType.js'
import { appAxios } from '@/axios.js'
import { paymentStatuses } from '@/configs/subscriptionStatuses.js'
import store from '@/store/store.js'
import {
  clearPendingExternalPayment,
  getPendingExternalPayment,
  shouldOpenPaymentExternally,
} from '@/utils/openPaymentUrl.js'
import { extractStartParam } from '@/utils/telegramLaunchParams.js'

const POLL_INTERVAL_MS = 3000
const POLL_MAX_MS = 60_000
// Параметры запуска живут всю сессию WebView — startapp=pay_<id> показываем один раз
const HANDLED_START_KEY = 'bb_handled_start_payment'

let pollTimer = null
let pollStartedAt = 0
let unbindListeners = null
let activationCheck = null

function findPaymentById(paymentId, subscriptions = store.state.user.profile?.subscriptions) {
  if (paymentId == null) {
    return null
  }

  for (const subscription of subscriptions || []) {
    const found = subscription.payments?.find(p => String(p.id) === String(paymentId))
    if (found) {
      return found
    }
  }

  return null
}

function readStartParamPaymentId() {
  // window.Telegram есть только с telegram-web-app.js — без него берём из параметров запуска
  const startParam = window.Telegram?.WebApp?.initDataUnsafe?.start_param || extractStartParam()
  if (!startParam || typeof startParam !== 'string') {
    return null
  }
  const match = /^pay_(.+)$/i.exec(startParam.trim())
  const paymentId = match?.[1] || null
  try {
    if (paymentId && sessionStorage.getItem(HANDLED_START_KEY) === paymentId) {
      return null
    }
  } catch {
    // ignore
  }
  return paymentId
}

/** startapp-платёж, ещё не показанный в этой сессии; помечает его показанным. */
function takeStartParamPaymentId() {
  const paymentId = readStartParamPaymentId()
  if (paymentId) {
    try {
      sessionStorage.setItem(HANDLED_START_KEY, paymentId)
    } catch {
      // ignore
    }
  }
  return paymentId
}

async function syncSubscriptionsFromApi() {
  const userId = store.state.user.profile?.id
  if (!userId) {
    return
  }

  const res = await appAxios.subscription.userSubscriptions(userId)
  store.commit('user/setProfile', {
    subscriptions: res.data.subscriptions,
  })
}

function ensurePaymentResultShown(paymentId) {
  const data = store.state.app.messageComponentData
  const sameId = data?.query?.payment_id != null
    && String(data.query.payment_id) === String(paymentId)

  if (store.state.app.messageComponent === 'PaymentResult' && sameId) {
    if (!data.external) {
      store.dispatch('app/setMessageComponent', {
        messageComponent: 'PaymentResult',
        messageComponentData: {
          ...data,
          query: { ...(data.query || {}), payment_id: paymentId },
          external: true,
        },
      })
    }
    return
  }

  store.dispatch('app/setMessageComponent', {
    messageComponent: 'PaymentResult',
    messageComponentData: {
      query: { payment_id: paymentId },
      external: true,
    },
  })
}

function stopPendingPaymentPoll() {
  if (pollTimer) {
    clearInterval(pollTimer)
    pollTimer = null
  }
  pollStartedAt = 0
}

async function refreshPendingExternalPaymentStatus(paymentId = getPendingExternalPayment()?.paymentId) {
  if (!paymentId) {
    return null
  }

  try {
    await syncSubscriptionsFromApi()
  } catch (e) {
    console.warn('external payment: subscriptions refresh failed', e)
    return null
  }

  const payment = findPaymentById(paymentId)
  if (
    payment?.status === paymentStatuses.success
    || payment?.status === paymentStatuses.failed
  ) {
    clearPendingExternalPayment()
    stopPendingPaymentPoll()
  }

  return payment
}

function startPendingPaymentPoll() {
  stopPendingPaymentPoll()
  pollStartedAt = Date.now()

  pollTimer = setInterval(async () => {
    if (Date.now() - pollStartedAt >= POLL_MAX_MS) {
      stopPendingPaymentPoll()
      return
    }

    const pending = getPendingExternalPayment()
    if (!pending?.paymentId) {
      stopPendingPaymentPoll()
      return
    }

    await refreshPendingExternalPaymentStatus()
  }, POLL_INTERVAL_MS)
}

async function checkPaymentOnActivation() {
  if (!shouldOpenPaymentExternally() && !getPendingExternalPayment()) {
    return
  }

  const pending = getPendingExternalPayment()
  const startPaymentId = takeStartParamPaymentId()
  const paymentId = pending?.paymentId || startPaymentId

  if (!paymentId) {
    return
  }

  ensurePaymentResultShown(paymentId)
  await refreshPendingExternalPaymentStatus(paymentId)

  if (getPendingExternalPayment()?.paymentId) {
    startPendingPaymentPoll()
  }
}

/** visibilitychange и visibility_changed из SDK приходят на один и тот же возврат — проверяем один раз. */
function onMiniAppBecameActive() {
  activationCheck ||= checkPaymentOnActivation().finally(() => {
    activationCheck = null
  })
  return activationCheck
}

/** После restore: поднять шторку, если Mini App убили во время оплаты в браузере. */
export async function restorePendingExternalPaymentOnBoot() {
  if (!store.getters['app/isBodybaseProject']) {
    return
  }

  const pending = getPendingExternalPayment()
  const startPaymentId = takeStartParamPaymentId()

  if (!pending && !startPaymentId) {
    return
  }

  if (!pending && startPaymentId) {
    // Deep link без localStorage — только id, url для «Открыть оплату» нет
    ensurePaymentResultShown(startPaymentId)
    await refreshPendingExternalPaymentStatus(startPaymentId)
    return
  }

  if (!shouldOpenPaymentExternally() && !window.Telegram?.WebApp) {
    // Браузерный заход с устаревшим storage — не мешаем
    return
  }

  ensurePaymentResultShown(pending.paymentId)
  await refreshPendingExternalPaymentStatus()
  if (getPendingExternalPayment()?.paymentId) {
    startPendingPaymentPoll()
  }
}

export function bindExternalPaymentReturnListeners() {
  if (unbindListeners) {
    return unbindListeners
  }

  const onActivated = () => {
    void onMiniAppBecameActive()
  }
  const onVisibility = () => {
    if (document.visibilityState === 'visible') {
      void onMiniAppBecameActive()
    }
  }

  // Событие activated из telegram-web-app.js приходит в SDK как visibility_changed
  const onVisibilityChanged = ({ is_visible: isVisible }) => {
    if (isVisible) {
      onActivated()
    }
  }
  let isBound = true
  let offVisibilityChanged = null
  loadTelegramSdk()
    .then((sdk) => {
      // SDK мог загрузиться уже после unbind
      if (isBound && sdk?.on) {
        offVisibilityChanged = sdk.on('visibility_changed', onVisibilityChanged)
      }
    })
    .catch((e) => {
      console.warn('external payment: Telegram SDK unavailable', e)
    })
  document.addEventListener('visibilitychange', onVisibility)

  unbindListeners = () => {
    isBound = false
    try {
      offVisibilityChanged?.()
    } catch {
      // ignore
    }
    document.removeEventListener('visibilitychange', onVisibility)
    stopPendingPaymentPoll()
    unbindListeners = null
  }

  return unbindListeners
}
